import {
  EAutopaymentError,
  MIN_PAYKEEPER_AMOUNT,
  VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT,
} from './definitions';

export default {
  topUpBalance: 'Top up the account',
  payKeeperSettingsModal: {
    title: 'Pay by card via the payment system',
    amount: 'Enter the amount to be credited',
    minAmount: `The minimum amount is ${MIN_PAYKEEPER_AMOUNT} {{currency}}`,
    chooseCard: 'Select a card',
    saveCard: 'Save the card for future payments',
    savedCard: 'Saved card',
    newCard: 'New card',
    replaceCard: 'Save a new card instead of the current one',
    acceptanceInfo: 'Clicking "Pay" will redirect you to the PayKeeper payment system website',
    pay: 'Pay',
  },
  payKeeperDeleteCardModal: {
    title: 'Delete saved card?',
    subtitle:
      'After deletion, you will need to re-enter your payment card details for the next payment',
    submit: 'Delete',
  },
  payKeeperAutopaymentInfo:
    'Automatically top up your account with <0>{{amount}}</0> <1>₽</1> on the last day of the month',
  payKeeperOffAutopaymentModal: {
    title: 'Disable auto payment for licenses?',
    subtitle:
      'If you disable auto payment, your licenses will not be renewed. With inactive licenses, you will not be able to use our service or send messages to your customers.',
    submitButton: 'Disable',
  },
  payKeeperAutopaymentSettingsModal: {
    title: 'Set up auto payment for licenses',
    editTitle: 'Change auto payment amount',
    amount: 'Enter auto payment amount',
    minAmount: `The minimum amount is ${MIN_PAYKEEPER_AMOUNT} ₽`,
    licensesInfo:
      'We recommend setting your autopayment amount to at least <0>{{threshold}}</0> ₽, which includes the cost of all your licenses, the minimum Viber payment, and the SMS subscriber fee (if applicable).\n\n' +
      'The auto payment will be debited from the specified card on the last day of the month.',
    validationInfo: `To validate the connected card, ${VALIDATION_PAYKEEPER_AUTOPAYMENT_AMOUNT}₽ will be deducted from its balance, which will be returned after the connection.\n\n`,
    acceptanceInfo:
      'Clicking the "Continue" button will redirect you to the PayKeeper payment service website.',
    lowSumWarning:
      'The auto payment amount that you set is not enough to cover the cost of the licenses',
  },
  payKeeperChangeCardCreditsModal: {
    title: 'Change auto payment card',
    subtitle:
      'To update your auto payment card information, enter the new details on the PayKeeper payment service page',
  },
  stripeAutopaymentSettingsModal: {
    titleConnect: 'Activate auto payment',
    titleEdit: 'Configure auto payment',
    threshold: 'When balance is below',
    amount: 'Replenish balance with',
    minThreshold: 'The minimum balance is {{threshold}} {{currency}}',
    minAmount: 'The minimum deposit is {{amount}} {{currency}}',
    acceptanceInfo: 'Once applied, you will be redirected to the Stripe payment system website',
    licensesInfo:
      'We recommend that you set a minimum balance of <0>{{threshold}} {{currency}}</0>. This is the cost of all your licenses and the minimum payment for Viber, if any.',
    incorrectSettingsWarning:
      'The amount you set is not enough to renew the licenses on time. There may be interruptions in the operation of the service if the bank debits funds multiple times',
  },
  stripeOffAutopaymentModal: {
    title: 'Deactivate auto payment?',
    subtitle:
      'If you deactivate the auto payment, your licenses will no longer be automatically renewed. This means that you will not be able to use our services and send messages to your customers.',
    submitButton: 'Request deactivation',
  },
  stripeAutopaymentSupportModal: {
    title: {
      on: 'Activate auto payment?',
      off: 'Deactivate auto payment?',
    },
    subtitle:
      'Auto payment allows you to automatically add a specified amount of money to your balance using a bank card. Are you sure?',
    submitButton: {
      on: 'Activate',
      off: 'Deactivate',
    },
  },
  stripeChargeAutopaymentModal: {
    title: 'Charge the unpaid invoice ',
    subtitle:
      'We’ll try to charge the unpaid invoice that previously failed due to an error. If there’s no open invoice, no charge will be made. Are you sure you want to proceed?',
    submitButton: 'Execute',
  },
  stripeChangeCardCreditsModal: {
    title: 'Edit card details for auto payment',
    subtitle:
      'To edit the linked card details, you will be redirected to the Stripe payment system website',
  },
  stripeAutopaymentInfo:
    'When the balance is <1>{{threshold}}</1> <0>{{currency}}</0> it will be automatically replenished with <2>{{amount}}</2> <0>{{currency}}</0>',
  autopaymentStatus: {
    connected: 'Auto payment activated',
    paused: 'Auto payment suspended',
    savedCard: 'Saved card',
  },
  actions: {
    topUp: 'Top up',
    generateInvoice: 'Generate invoice',
    payByCard: 'Pay by card',
    deleteCard: 'Delete card',
    deleteAutopayment: 'Delete auto payment',
    connectAutopayment: 'Activate auto payment',
    chargeAutopayment: 'Trigger charge',
    connectAutopaymentHint:
      'Auto payment allows you to automatically add a specified amount of money to your balance using a bank card',
    changeAutopaymentSettings: 'Edit auto payment',
    changeCardCredits: 'Edit card details',
    changeAmount: 'Change amount',
    offAutopayment: 'Deactivate auto payment',
    restoreAutopayment: 'Resume auto payment',
  },
  payKeeperThresholdWarning: {
    owner:
      'The amount of the auto payment is less than the amount of regular payments. We recommend increasing the auto payment to ensure that there are enough funds to renew the license. <0>Update the auto payment</0>',
    others:
      'The amount of the auto payment is less than the amount of regular payments. We recommend increasing the auto payment to ensure that there are enough funds to renew the license',
  },
  thresholdWarning:
    'The set minimum balance may not be enough to cover your regular payments. We recommend that you update your auto payment to ensure that your licenses are renewed on time. <0>Update the auto payment</0>',
  invoiceSettingsModal: {
    title: 'Generate payment invoice',
    amount: 'Enter the amount for which you would like to get an invoice',
    warning:
      'When paying, please take into account all third-party fees for transfers and currency exchange operations.',
    generateButton: 'Generate',
    insertSum: 'Insert sum',
  },
  invoiceGeneratedModal: {
    title: 'Your invoice is ready',
    content: `We'll update your account balance when your payment is received. To download your invoice later, go to <0>Documents.</0>`,
    download: 'Download invoice',
  },
  stripeSettingsModal: {
    title: 'Pay by card with <0></0> payment system',
    topUp: 'Checkout',
    amount: 'Enter the amount',
    cancel: 'Cancel',
    warning:
      'Your payment will be credited to your current balance based on the offer. For any inquiries or issues, contact us at support@edna.io',
    content:
      'After pressing the “Checkout” button, you will be redirected to Stripe Checkout page to make your payment.',
  },
  errors: {
    [EAutopaymentError.INSUFFICIENT_FUNDS]:
      'The charge failed on {{date}}. Insufficient funds. Recharge your card within {{leftDays}} {{leftDays1}} to resume the auto payment. If the balance is insufficient after {{leftDays}} {{leftDays2}}, the auto payment will be disabled and the services will be suspended. The next charge will be on {{nextDate}}',
    [EAutopaymentError.CARD_DECLINE]:
      'Failed to make an auto payment with the linked card. Check the card details',
    [EAutopaymentError.AUTHENTICATION_REQUIRED]:
      'The auto payment failed on {{date}}. Your bank has requested payment confirmation. To confirm the payment, go to the <0>Stripe payment system website</0>.',
    payKeeperAutopayment: 'Error when adding funds to the account via card on {{date}}',
    payKeeperAutopaymentTenantBlocked:
      'Your account is currently inactive. Auto payment will resume once your account is activated',
  },
};
